import React, { useEffect, useRef, useState } from "react";

/** Set to true locally to delay children by DEBUG_DELAY_MS after they become
 * visible, making the placeholder swap easy to see. Must stay false in commits. */
const DEBUG = false;
const DEBUG_DELAY_MS = 1500;

// // // //

export interface RenderWhenVisibleProps {
    children: React.ReactNode;
    /** Estimated placeholder height (px) used before children have rendered
     * for the first time. Once children render, their measured height is used
     * instead, so this only needs to be roughly right. */
    placeholderHeight?: number;
    placeholderClassName?: string;
    /** Margin around the viewport used when deciding visibility. The default
     * mounts children half a viewport before they scroll into view so the
     * placeholder swap happens offscreen. */
    rootMargin?: string;
    /** When true, children stay mounted after first becoming visible instead
     * of unmounting again when scrolled back out of the viewport. */
    once?: boolean;
}

/**
 * RenderWhenVisible
 * Only mounts children while the component is (nearly) within the viewport,
 * swapping in a fixed-height placeholder when offscreen. Use it to keep
 * expensive subtrees (e.g. Highcharts instances) out of the DOM on long pages.
 *
 * Layout shift is avoided by measuring the rendered children just before they
 * unmount and giving the placeholder that exact height — props.placeholderHeight
 * only seeds the estimate for children that have never rendered.
 */
function RenderWhenVisibleImpl(props: RenderWhenVisibleProps) {
    const {
        children,
        placeholderHeight,
        placeholderClassName,
        rootMargin = "50% 0px",
        once = false,
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    // Mirrors the `visible` state so the observer callback can tell whether
    // children are currently mounted without re-subscribing on every toggle.
    const visibleRef = useRef(false);
    const [visible, setVisible] = useState(false);
    const [measuredHeight, setMeasuredHeight] = useState<number | undefined>(
        undefined,
    );

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // Browsers without IntersectionObserver: render children unconditionally.
        // (Initializing state this way instead would mismatch SSR hydration.)
        if (typeof IntersectionObserver === "undefined") {
            visibleRef.current = true;
            // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time capability fallback, no cascading-render risk
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    visibleRef.current = true;
                    setVisible(true);
                    if (once) {
                        observer.disconnect();
                    }
                    return;
                }

                // Leaving the viewport: the callback fires while the children
                // are still in the DOM, so offsetHeight is the true content
                // height — capture it for the placeholder before unmounting.
                if (visibleRef.current) {
                    setMeasuredHeight(container.offsetHeight);
                }
                visibleRef.current = false;
                setVisible(false);
            },
            { rootMargin },
        );

        observer.observe(container);

        return () => {
            observer.disconnect();
        };
    }, [rootMargin, once]);

    return (
        <div ref={containerRef}>
            {visible ? (
                children
            ) : (
                <div
                    className={placeholderClassName}
                    style={{ height: measuredHeight ?? placeholderHeight }}
                />
            )}
        </div>
    );
}

// // // //

/* eslint-disable react/no-multi-comp -- debug-only helpers kept next to the component they wrap */

/**
 * RenderWhenVisibleDebug
 * Same as RenderWhenVisible, but each time the children mount they first show
 * a placeholder text for DEBUG_DELAY_MS. Only used when DEBUG is true.
 */
function RenderWhenVisibleDebug(props: RenderWhenVisibleProps) {
    return (
        <RenderWhenVisibleImpl {...props}>
            <DelayedChildren placeholderHeight={props.placeholderHeight}>
                {props.children}
            </DelayedChildren>
        </RenderWhenVisibleImpl>
    );
}

function DelayedChildren(props: {
    children: React.ReactNode;
    placeholderHeight?: number;
}) {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => setReady(true), DEBUG_DELAY_MS);
        return () => clearTimeout(timeout);
    }, []);

    if (ready) {
        return props.children;
    }

    return (
        <div
            className="text-xs text-muted-foreground"
            style={{ height: props.placeholderHeight }}
        >
            {"RenderWhenVisible (debug): waiting…"}
        </div>
    );
}

// // // //

// Picked once at module load, so production (DEBUG = false) renders
// RenderWhenVisibleImpl directly with no debug state, effects, or timers.
export const RenderWhenVisible = DEBUG
    ? RenderWhenVisibleDebug
    : RenderWhenVisibleImpl;
